import React, { useEffect, useState } from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _classNames from 'classnames';
import {
  Box,
  IconButton,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import { contentTypeMapping } from 'components/RichTextReactRenderer/utils';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { makeSelectAllowSignIn } from 'containers/Main/selectors';
import { windowScrollTo } from 'utils/embedded';
import { SeriesVideoPreview } from './preview/SeriesVideoPreview';
import { SeriesPodcastPreview } from './preview/SeriesPodcastPreview';
import { getResourceType } from '../utils';
import { SeriesResourcePreview } from './preview/SeriesResourcePreview';
import { SeriesResourceDetails } from './SeriesResourceDetails';
import { SeriesArticlePreview } from './preview/SeriesArticlePreview';
import { SeriesResourceCardActions } from './SeriesResourceCardActions';
import { SeriesAssessmentPreview } from './preview/SeriesAssessmentPreview';

const useStyles = makeStyles(theme => ({
  paper: {
    background: 'linear-gradient(81deg, #D4F6FF 0%, #D3ECFF 100%)',
    borderRadius: props => !props.isMobile && '8px !important',
    padding: props => !props.isMobile && '30px',
    display: 'flex',
    flexDirection: 'column',
    minHeight: props => !props.isMobile && 330,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  contentContainer: {
    display: 'flex',
    marginBottom: 20,

    [theme.breakpoints.down(800)]: {
      flexDirection: 'column',
      marginBottom: 10,
    },

    '&.video': {
      [theme.breakpoints.up(800)]: {
        '& .preview': {
          width: '45%',
          marginRight: '5%',
          flexShrink: 0.3,
        },
      },
    },

    '&.podcast': {
      [theme.breakpoints.between(800, 1600)]: {
        '& .preview': {
          width: '50%',
          marginRight: '5%',
          flexShrink: 0.15,
        },
      },

      [theme.breakpoints.up(1600)]: {
        flexDirection: 'column',
      },

      '& .preview': {
        [theme.breakpoints.down(800)]: {
          width: 120,
          height: 125,
          alignSelf: 'center',
        },

        [theme.breakpoints.up(1600)]: {
          marginBottom: 20,
        },
      },
    },

    '& .preview': {
      [theme.breakpoints.down(800)]: {
        marginBottom: 15,
      },
    },
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    overflow: 'hidden',
    padding: props => props.isMobile && '0px 20px 0px',
    [theme.breakpoints.up(800)]: {
      width: props => props.type === 'video' && '55%',
    },
    [theme.breakpoints.between(800, 1600)]: {
      width: props => props.type === 'podcast' && '50%',
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    '& h5': {
      fontSize: props => props.isMobile && props.fontSize,
      marginRight: 8,
    },
  },
  richText: {
    '& p, & span[role="button"]': {
      fontSize: props => props.fontSize,
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: props => (props.isMobile ? 'space-between' : 'flex-end'),
    padding: props => props.isMobile && '0px 20px 20px',
  },
  openLink: {
    display: 'inline-flex',
    marginTop: 2,

    '& svg': {
      fontSize: props => props.fontSize,
    },
  },
  iconButton: {
    alignSelf: 'flex-start',
    padding: 6,
  },
}));

const allowSignInSelector = makeSelectAllowSignIn();

export const SeriesResourceCard = ({
  series,
  isDesktop,
  isBigTablet,
  isTablet,
  isMobile,
  resource,
  handleCompleteChange,
  handleNext,
  seriesSiteCopy,
  trackingProps,
  resources,
  completedResourcesIds,
  index,
  fontSize,
  onProgressUpdate,
  resourcesProgress,
  completeResource,
  onSeriesComplete,
  isSeriesComplete,
  completeSeriesAssessment,
  completedAssessments,
  isManualSeriesCompletion,
  linkAsPlainText,
  showActions = true,
  showOpenInNewIcon = true,
  showShareAction,
  renderHeartButton,
  assessmentSpanFullWidth,
}) => {
  const [triggerResetShortMode, setTriggerResetShortMode] = useState();
  const [podcastEpisodeSiteCopy] = useSiteCopySelector(['podcast-episodes']);
  const allowSignIn = useSelector(allowSignInSelector);
  const type = getResourceType(resource);
  const classes = useStyles({ isMobile, fontSize, type });

  const title =
    _get(resource, 'fields.name') || _get(resource, 'fields.title', '');
  const description =
    _get(resource, 'fields.description') ||
    _get(resource, 'fields.shortDescription');
  const completed = completedResourcesIds.includes(_get(resource, 'sys.id'));

  const onChange = e => {
    handleCompleteChange(resource, e.target.checked);
  };

  const resourcePathname = `/${contentTypeMapping[type]}/${_get(
    resource,
    'fields.slug',
  )}`;

  const scrollToResource = isExpanded => {
    if (!isExpanded) {
      windowScrollTo(0, 0);
    }
  };

  const resourceId = _get(resource, 'sys.id');
  useEffect(() => {
    if (resourceId) {
      setTriggerResetShortMode(Math.random());
    }
  }, [resourceId]);

  if (_isEmpty(resource)) return null;

  const actionNodesProps = {
    seriesSiteCopy,
    completedResourcesIds,
    resource,
    resources,
    onSeriesComplete,
    handleNext,
    activeResourceIdx: index,
    completed,
    onChange,
    fontSize,
    isSeriesComplete,
    isManualSeriesCompletion,
  };

  const actionsNode = showActions ? (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <SeriesResourceCardActions {...actionNodesProps} />
  ) : null;

  if (['blog', 'news', 'article'].includes(type)) {
    return (
      <SeriesArticlePreview
        isMobile={isMobile}
        title={title}
        description={description}
        resource={resource}
        seriesSiteCopy={seriesSiteCopy}
        type={type}
        allowSignIn={allowSignIn}
        fontSize={fontSize}
        completeResource={completeResource}
        completed={completed}
        resourcePathname={resourcePathname}
        actionsNode={actionsNode}
        showOpenInNewIcon={showOpenInNewIcon}
        showShareAction={showShareAction}
        enableScrollCompletion={
          completedResourcesIds.length + 1 < resources.length
        }
        renderHeartButton={renderHeartButton}
      />
    );
  }

  const assessmentActionNodes = {
    ...actionNodesProps,
    showCompleteCheckbox: false,
  };

  const assessmentActionsNode = (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <SeriesResourceCardActions {...assessmentActionNodes} />
  );

  if (type === 'assessments') {
    return (
      <SeriesAssessmentPreview
        item={resource}
        completeSeriesAssessment={completeSeriesAssessment}
        completedAssessments={completedAssessments}
        series={series}
        showShareButton={showShareAction}
        actionsNode={assessmentActionsNode}
        linkAsPlainText={linkAsPlainText}
        small={isMobile || isTablet}
        trackingProps={trackingProps}
        spanFullWidth={assessmentSpanFullWidth}
      />
    );
  }

  const initialTime = completed
    ? null
    : _get(resourcesProgress, _get(resource, 'sys.id'), 0);

  return (
    <Paper elevation={0} className={classes.paper} id="series-resource-card">
      <Box className={classes.container}>
        <div className={_classNames(classes.contentContainer, type)}>
          <div className="preview">
            {type === 'video' ? (
              <SeriesVideoPreview
                isMobile={isMobile}
                item={resource}
                trackingProps={trackingProps}
                onProgressUpdate={onProgressUpdate}
                initialTime={initialTime}
                completed={completed}
              />
            ) : type === 'podcast' ? (
              <SeriesPodcastPreview
                isDesktop={isDesktop}
                isBigTablet={isBigTablet}
                isTablet={isTablet}
                isMobile={isMobile}
                item={resource}
                trackingProps={trackingProps}
                podcastEpisodeSiteCopy={podcastEpisodeSiteCopy}
                onProgressUpdate={onProgressUpdate}
                initialTime={initialTime}
                completed={completed}
              />
            ) : (
              <SeriesResourcePreview
                isMobile={isMobile}
                item={resource}
                trackingPath={trackingProps.path}
                type={type}
              />
            )}
          </div>
          <div className={classes.infoContainer}>
            <Box className={classes.titleContainer}>
              <Typography color="textPrimary" variant="h5">
                {title}
              </Typography>
              {showOpenInNewIcon && (
                <IconButton className={classes.iconButton} aria-label="Open">
                  <Link
                    to={resourcePathname}
                    target="_blank"
                    className={classes.openLink}
                  >
                    <OpenInNewIcon />
                  </Link>
                </IconButton>
              )}
            </Box>
            <SeriesResourceDetails
              resource={resource}
              resourcePathname={resourcePathname}
              seriesSiteCopy={seriesSiteCopy}
              allowSignIn={allowSignIn}
              type={type}
              fontSize={fontSize}
              isMobile={isMobile}
              showShareAction={showShareAction}
              renderHeartButton={renderHeartButton}
            />
            {typeof description === 'object' ? (
              <RichTextReactRenderer
                className={classes.richText}
                data={description}
                limit={isMobile ? 85 : 200}
                linkAsPlainText
                triggerResetShortMode={triggerResetShortMode}
                readMoreLessHandler={scrollToResource}
              />
            ) : (
              <Typography>{description}</Typography>
            )}
          </div>
        </div>
        <Box className={classes.footer}>{actionsNode}</Box>
      </Box>
    </Paper>
  );
};
